import { useState, useEffect, useCallback, useRef } from 'react';
import { useSpring, animated } from 'react-spring';
import springConfig from 'lib/utils/springConfigs';
import supportsIntersectionObserver from 'lib/supportsIntersectionObserver';

const useOnScreen = (ref, rootMargin = '0px') => {
  const [isIntersecting, setIntersecting] = useState(false);

  useEffect(() => {
    const { current } = ref;

    if (!supportsIntersectionObserver()) return () => {};

    const observer = new IntersectionObserver(
      ([entry]) => {
        setIntersecting(entry.isIntersecting);
      },
      { rootMargin }
    );

    if (current) observer.observe(current);

    return () => {
      observer.unobserve(current);
    };
  }, [ref, rootMargin]);

  return isIntersecting;
};

const Parallax = ({
  children,
  speed,
  offset = 0,
  className,
  disableMobile = false,
  rotate = null,
}) => {
  const ref = useRef();
  const onScreen = useOnScreen(ref);
  const [scrollY, setScrollY] = useState(0);
  const [{ springScrollY }, setSpringScrollY] = useSpring(() => ({
    springScrollY: 0,
    config: springConfig.easeInButSlower,
  }));

  const isWindow = typeof window !== 'undefined';

  const getYByBoost = boost => scrolled => {
    if (disableMobile && isWindow && window.innerWidth < 768) return 0;
    if (boost === 0) return scrolled + offset;
    if (boost === 1) return 0 + offset;
    return scrolled + offset - scrolled * boost;
  };

  const getY = getYByBoost(speed);

  const getDegByBoost = boost => scrolled => {
    if (disableMobile && isWindow && window.innerWidth < 768) return 0;
    if (boost === 0) return scrolled;
    if (boost === 1) return 0;
    return scrolled - scrolled * boost;
  };

  const getDeg = getDegByBoost(speed);

  const interpHeader = springScrollY.to(n =>
    rotate
      ? `translateY(${getY(n * -1)}px) rotate(${getDeg((n * -1) / 8) - rotate}deg)`
      : `translateY(${getY(n * -1)}px)`
  );

  useEffect(() => {
    setSpringScrollY({ springScrollY: scrollY });
  }, [setSpringScrollY, scrollY]);

  const handleScroll = useCallback(() => {
    if (!onScreen || !ref.current) return;
    setScrollY(Math.round(ref.current.getBoundingClientRect().top));
  }, [setScrollY, onScreen]);

  useEffect(() => {
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);

  return (
    <div ref={ref} className={`${className} overflow-visible`}>
      <animated.div style={{ transform: interpHeader }} className="will-change-transform">
        {children}
      </animated.div>
    </div>
  );
};

export default Parallax;
